import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useOrg } from "./session";

export interface Feature {
  key: string;
  category: string;
  name: string;
  description?: string;
  kind: "toggle" | "limit";
  unit?: string;
  period?: string;
  default_enabled?: boolean;
  default?: number | null;
  min?: number;
  max?: number;
  allow_unlimited?: boolean;
}

export interface Setting {
  enabled?: boolean;
  limit?: number | null;
  unlimited?: boolean;
}

export interface EntitlementValue {
  key: string;
  kind: "toggle" | "limit";
  enabled: boolean;
  limit: number | null;
  source: "plan" | "override" | "default" | "none";
}

export interface Entitlements {
  active: boolean;
  plan_name?: string;
  plan_version?: number;
  billing_interval?: string;
  features: Record<string, EntitlementValue>;
}

// The enterprise's plan, used to show only what it includes.
export function useEntitlements() {
  const { base, orgId } = useOrg();
  const q = useQuery({ queryKey: [orgId, "entitlements"], queryFn: () => api<Entitlements>(`${base}/entitlements`), staleTime: 30_000 });
  return {
    ...q,
    enabled: (key: string) => q.data?.features[key]?.enabled ?? false,
    limit: (key: string) => q.data?.features[key]?.limit ?? undefined,
  };
}

// Currencies with three minor digits (fils) vs the usual two.
const minorDigits: Record<string, number> = { BHD: 3, KWD: 3, OMR: 3, JOD: 3, TND: 3 };
export const digits = (currency: string) => minorDigits[currency] ?? 2;
export const toMinor = (major: string, currency: string) => (major.trim() === "" ? null : Math.round(Number(major) * 10 ** digits(currency)));
export const toMajor = (minor: number | null | undefined, currency: string) => (minor == null ? "" : String(Number((minor / 10 ** digits(currency)).toFixed(digits(currency)))));
/** money shows an amount in its currency: "60 BHD", "12.500 BHD". Whole
 *  amounts drop the decimals; otherwise all of the currency's digits show. */
export const money = (minor: number | null | undefined, currency: string) => {
  if (minor == null) return "—";
  const d = digits(currency);
  const whole = minor % 10 ** d === 0;
  return `${(minor / 10 ** d).toLocaleString("en-US", { minimumFractionDigits: whole ? 0 : d, maximumFractionDigits: d })} ${currency}`;
};

export function groupByCategory(features: Feature[]) {
  const groups = new Map<string, Feature[]>();
  for (const f of features) groups.set(f.category, [...(groups.get(f.category) ?? []), f]);
  return [...groups.entries()];
}
